"use client";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { usePassportProvider } from "@/context";
import {
  Action,
  ControlType,
} from "@/context/AnalyticsProvider/AnalyticsProvider";
import {
  GamePageSection,
  useGamePageAnalytics,
} from "@/context/AnalyticsProvider/gamePage";
import { useGameDetailsContext } from "@/context/GameDetailsContext";
import { GameOnboardingProvider } from "@/context/GameOnboardingContext";
import { cn } from "@/lib/utils";
import { isSafari } from "@/utils/browser";
import { XIcon } from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GlassButton } from "../GlassButton";
import { AnimatedEffect } from "./AnimatedEffect";
import { BronzeRewardAchieved } from "./BronzeRewardAchieved";
import { CrateProgressBar } from "./CrateProgressBar";
import { FinalReward } from "./FinalReward";
import { FollowOnX } from "./FollowOnX";
import { GPOnboardingLogin } from "./GPOnboardingLogin";
import { LinkSteam } from "./LinkSteam";
import { OnboardingExtrinsic } from "./OnboardingExtrinsic";
import { OnboardingHybrid } from "./OnboardingHybrid";
import { OnboardingInGame } from "./OnboardingInGame";
import { OnboardingLinkTree } from "./OnboardingLinkTree";
import { SilverRewardAchieved } from "./SilverRewardAchieved";
import { WishlistSteam } from "./WishlistSteam";
import { WishlistSteamVerify } from "./WishlistSteamVerify";
import { GPExperimentType, GPOnboardingStep } from "./utils";

interface GPOnboardingModalProps {
  open: boolean;
  onClose: () => void;
  experimentType?: GPExperimentType;
}

// Main Modal Component
export function GPOnboardingModal({
  open,
  onClose,
  experimentType,
}: GPOnboardingModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [hasCompletedOnboardingScreen, setHasCompletedOnboardingScreen] =
    useState(false);
  const [isWishlistVerified, setIsWishlistVerified] = useState(false);
  const { isLoggedIn, isLoggingIn, walletAddress } = usePassportProvider();
  const { track } = useGamePageAnalytics();
  const { addToWishlist, isWishlisted } = useGameDetailsContext();

  const autoWishlistRef = useRef<boolean>(isWishlisted);

  useEffect(() => {
    if (!open || !walletAddress || autoWishlistRef.current) return;
    const addToWishlistAction = async () => {
      await addToWishlist();
      autoWishlistRef.current = true;
    };
    addToWishlistAction();
  }, [walletAddress, addToWishlist, open]);

  // Determine if we should show onboarding splash screen
  const showSplashScreen = useMemo(() => {
    if (!experimentType || hasCompletedOnboardingScreen) return false;
    return Object.values(GPExperimentType).includes(experimentType);
  }, [experimentType, hasCompletedOnboardingScreen]);

  useEffect(() => {
    if (!open) return;

    const control: Lowercase<string> = showSplashScreen
      ? "step-get-started"
      : (`step-${GPOnboardingScreens[currentStep].key?.toLowerCase()}` as Lowercase<string>);

    track({
      action: Action.VIEWED,
      extras: {
        control,
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });
  }, [track, currentStep, showSplashScreen, open]);

  const handleNext = useCallback(() => {
    // If we're showing an onboarding screen and haven't completed it yet,
    // transition to normal flow instead of incrementing step
    if (showSplashScreen) {
      setHasCompletedOnboardingScreen(true);
      return;
    }

    setCurrentStep((prev) => {
      // Skip Wishlist Verify step if wishlist is already verified
      // Current step 4 is SilverRewardAchieved. Next is 5 (Verify) or 6 (FollowOnX)
      if (prev === 4 && isWishlistVerified) {
        return prev + 2;
      }
      return prev + 1;
    });
  }, [showSplashScreen, isWishlistVerified]);

  const handleClose = useCallback(
    (open: boolean) => {
      // if modal becomes open from closed, ignore handleClose
      if (open) {
        track({
          action: Action.OPENED,
          extras: {
            control: "onboarding-modal",
          },
        });
        return;
      }

      track({
        action: Action.CLOSED,
        extras: {
          control: "onboarding-modal",
        },
      });

      setCurrentStep(0);
      onClose();
    },
    [onClose, track],
  );

  const GPOnboardingScreens = useMemo(
    () => [
      <GPOnboardingLogin
        key={GPOnboardingStep.LOGIN}
        onNext={handleNext}
        isVisible={currentStep === 0 && open}
        track={track}
      />,
      <BronzeRewardAchieved
        key={GPOnboardingStep.BRONZE_REWARD_ACHIEVED}
        onNext={handleNext}
        isVisible={currentStep === 1 && open}
        track={track}
      />,
      <WishlistSteam
        key={GPOnboardingStep.WISHLIST_STEAM}
        onNext={handleNext}
        isVisible={currentStep === 2 && open}
        track={track}
      />,
      <LinkSteam
        key={GPOnboardingStep.LINK_STEAM}
        onNext={handleNext}
        isVisible={currentStep === 3 && open}
        track={track}
        setIsWishlistVerified={setIsWishlistVerified}
      />,
      <SilverRewardAchieved
        key={GPOnboardingStep.SILVER_REWARD_ACHIEVED}
        onNext={handleNext}
        isVisible={currentStep === 4 && open}
        track={track}
      />,
      <WishlistSteamVerify
        key={GPOnboardingStep.WISHLIST_STEAM_VERIFY}
        onNext={handleNext}
        isVisible={currentStep === 5 && open}
        track={track}
      />,
      <FollowOnX
        key={GPOnboardingStep.FOLLOW_ON_X}
        onNext={handleNext}
        isVisible={currentStep === 6 && open}
        track={track}
      />,
      <FinalReward
        key={GPOnboardingStep.GOLD_REWARD_ACHIEVED}
        onNext={onClose}
        isVisible={currentStep === 7 && open}
        track={track}
      />,
    ],
    [handleNext, currentStep, onClose, track, open],
  );

  // Determine which onboarding splash screen to show when onboardingType is set
  const GPOnboardingSplashScreen = useCallback(() => {
    switch (experimentType) {
      case GPExperimentType.EXTRINSIC:
      case GPExperimentType.NO_PROMPT_EXTRINSIC:
        return (
          <OnboardingExtrinsic onNext={handleNext} track={track} isVisible />
        );

      case GPExperimentType.HYBRID:
        return <OnboardingHybrid onNext={handleNext} track={track} isVisible />;

      case GPExperimentType.INGAME:
      case GPExperimentType.NO_PROMPT:
      case GPExperimentType.NO_PROMPT_INGAME:
        return <OnboardingInGame onNext={handleNext} track={track} isVisible />;

      case GPExperimentType.LINK_TREE:
        return (
          <OnboardingLinkTree onNext={handleNext} track={track} isVisible />
        );

      default:
        return null;
    }
  }, [experimentType, handleNext, track]);

  const handlePrizeImgClick = useCallback(() => {
    track({
      action: Action.PRESSED,
      extras: {
        control: "prize",
        controlType: ControlType.IMAGE,
        section: GamePageSection.ONBOARDING_MODAL,
        controlLabel:
          currentStep >= 7
            ? "Gold Prize"
            : currentStep >= 4
              ? "Silver Prize"
              : currentStep >= 1
                ? "Bronze Prize"
                : "Default Prize",
      },
    });
  }, [track, currentStep]);

  const handleLogoClick = useCallback(() => {
    track({
      action: Action.PRESSED,
      extras: {
        control: "logo",
        controlType: ControlType.IMAGE,
        section: GamePageSection.ONBOARDING_MODAL,
        controlLabel: "8 Ball Logo",
      },
    });
  }, [track]);

  const handleProgressBarClick = useCallback(
    (label: string) => {
      track({
        action: Action.PRESSED,
        extras: {
          control: "progress-bar",
          controlLabel: label,
          section: GamePageSection.ONBOARDING_MODAL,
        },
      });
    },
    [track],
  );

  // Lock body scroll when modal is open since we are using modal={false}
  useEffect(() => {
    if (open) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [open]);

  const isVerifyStep = currentStep === 5;
  const isLoginStep = currentStep === 0;
  const isCrateProgressStep =
    currentStep === 1 || currentStep === 4 || currentStep === 7;

  return (
    <GameOnboardingProvider>
      {/* Custom Overlay to prevent flickering when switching modal modes */}
      <div
        className={cn(
          "fixed inset-0 z-[99] bg-black/40 transition-opacity duration-200",
          open ? "opacity-100" : "opacity-0 pointer-events-none",
          isLoggingIn && "pointer-events-none",
        )}
      />
      {/* modal={false} prevents the focus trap and allows outside interactions (Passport iframe) */}
      <Dialog open={open} onOpenChange={handleClose} modal={false}>
        <DialogContent
          className={cn(
            "font-suisse flex gap-y-0 flex-col !z-[100] bg-neutral-900/30 backdrop-blur-[32px] border-0",
            "h-[100dvh] w-screen max-h-none max-w-none !rounded-none", // Mobile full screen
            "md:glass-border md:h-[95vh] md:w-[95vw] md:!rounded-[2.5rem] md:max-h-[50rem] md:max-w-xl", // Desktop modal
            showSplashScreen
              ? "p-0 overflow-hidden"
              : cn(
                  "px-[clamp(0.5rem,12.5vw,5rem)] py-[clamp(1rem,6.25vh,3rem)]",
                  !isVerifyStep && !isLoginStep && "pb-[clamp(4rem,12vh,6rem)]", // Increased bottom padding for non-verify steps
                ),
          )}
          style={isLoggingIn ? { pointerEvents: "none" } : undefined}
          removeCloseButton
          // Hide the native overlay since we are using a custom one
          overlayClassName="hidden"
          onInteractOutside={(e) => e.preventDefault()}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogTitle className="sr-only">
            Custom Steam Onboarding Modal
          </DialogTitle>

          {showSplashScreen ? (
            // Show onboarding splash screen
            <GPOnboardingSplashScreen />
          ) : (
            // Normal flow with all steps
            <>
              {!isVerifyStep && (
                <div className="relative mx-auto h-fit w-full">
                  {!isCrateProgressStep ? (
                    <Image
                      src="/assets/steam-onboarding/logo-and-prizes.webp"
                      alt="Immutable 8 Ball Rewards"
                      width={500}
                      height={333}
                      className="w-[full] mx-auto h-auto object-contain drop-shadow-2xl relative z-10"
                      priority
                    />
                  ) : (
                    <>
                      <AnimatedEffect
                        className="absolute w-full h-full"
                        tint="#D37A38"
                      />
                      <div className="relative mx-auto w-full max-w-[16.125rem] h-[clamp(9rem,24vh,15.125rem)]">
                        <Image
                          src={
                            currentStep === 7
                              ? "/assets/steam-onboarding/crate-gold.webp"
                              : currentStep === 4
                                ? "/assets/steam-onboarding/crate-silver.webp"
                                : "/assets/steam-onboarding/crate-bronze.webp"
                          }
                          alt="8 Ball loot crate"
                          width={258}
                          height={242}
                          className="drop-shadow-2xl object-contain w-full h-full"
                          onClick={handlePrizeImgClick}
                        />
                      </div>

                      {!isLoggedIn ? (
                        <Image
                          width={330}
                          height={111}
                          src="/assets/steam-onboarding/logo.webp"
                          alt="Immutable 8 Ball Rewards"
                          className="-bottom-6 left-1/2 -translate-x-1/2 absolute w-3/5 max-w-[20.625rem] h-auto"
                          onClick={handleLogoClick}
                        />
                      ) : null}
                    </>
                  )}
                </div>
              )}
              {/* If Crate progress screen */}
              {isCrateProgressStep ? (
                <CrateProgressBar
                  currentStep={currentStep}
                  handleProgressBarClick={handleProgressBarClick}
                />
              ) : null}

              {/* Onboarding Steps */}
              <div className="relative overflow-hidden flex-grow">
                <div
                  className={cn(
                    "flex h-full transition-transform duration-500 ease-in-out",
                    isVerifyStep ? "items-start md:items-center" : "",
                  )}
                  style={{
                    transform: `translateX(-${currentStep * 100}%)`,
                  }}
                >
                  {GPOnboardingScreens.map(
                    (Step: JSX.Element, index: number) => (
                      <div
                        key={Step.key}
                        className={cn(
                          "w-full h-full flex-shrink-0 transition-opacity duration-500 ease-in-out flex text-center",
                          index === currentStep ? "opacity-100" : "opacity-0",
                        )}
                        style={{
                          // safari hides auto content on blur incorrectly, revert to visible
                          contentVisibility: isSafari() ? "visible" : "auto",
                        }}
                      >
                        {Step}
                      </div>
                    ),
                  )}
                </div>
              </div>

              <DialogClose
                className="absolute right-4 top-4"
                onClick={onClose}
                asChild
              >
                <GlassButton
                  control="close-modal"
                  section={GamePageSection.ONBOARDING_MODAL}
                  className="size-10 bg-white/10"
                >
                  <XIcon className="size-4" />
                </GlassButton>
              </DialogClose>
            </>
          )}
        </DialogContent>
      </Dialog>
    </GameOnboardingProvider>
  );
}
